import React, { createContext, useContext, useState, useEffect } from "react";
import { apiList, invokeApi } from "../services/apiServices";
import { config } from "../config/config";
import { useCookies } from "react-cookie";

const NavContext = createContext();

export const useNav = () => useContext(NavContext);

export const NavProvider = ({ children }) => {
    const [cookies] = useCookies();
    const [programmeItems, setProgrammeItems] = useState([]);
    const [publicationItems, setPublicationItems] = useState([]);
    const [peopleItems, setPeopleItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProgramme = async () => {
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.getProgrammes,
                null,
                cookies
            );
            console.log("NavContext: Programme API Response:", response);
            if (response?.status === 200) {
                const resData = response.data.programmes;
                console.log("NavContext: Programme resData:", resData);
                let allProgrammes = [];

                if (Array.isArray(resData)) {
                    allProgrammes = resData;
                } else if (resData?.data && Array.isArray(resData.data)) {
                    allProgrammes = resData.data;
                } else if (resData?.programmes && Array.isArray(resData.programmes)) {
                    allProgrammes = resData.programmes;
                } else if (resData?.items && Array.isArray(resData.items)) {
                    allProgrammes = resData.items;
                }

                const dynamicProgrammeItems = allProgrammes.map((p) => ({
                    label: p.programmeTitle || p.title || p.name || p.programmeName || "No Title",
                    path: `/programme/${p.ogUrl}`,
                }));

                if (dynamicProgrammeItems.length > 0) {
                    setProgrammeItems(dynamicProgrammeItems);
                } else {
                    console.log("NavContext: No programmes found in 200 response, using fallback.");
                    setProgrammeItems([
                        { label: "Bio Diversity Documentation", path: "/biodiversity-of-lakshadweep" },
                        { label: "Coral Reef Restoration", path: "/restoring-hope" },
                        { label: "Community and Resource Governance", path: "/community-resource" },
                        { label: "Community wellbeing and Environmental", path: "/community-welbeing" },
                        { label: "Sustainable Fisheries", path: "/sustainable-fisheries" },
                    ]);
                }
            }
        } catch (error) {
            console.error("Programme fetch error:", error);
            setProgrammeItems([
                { label: "Bio Diversity Documentation", path: "/biodiversity-of-lakshadweep" },
                { label: "Coral Reef Restoration", path: "/restoring-hope" },
                { label: "Community and Resource Governance", path: "/community-resource" },
                { label: "Community wellbeing and Environmental", path: "/community-welbeing" },
                { label: "Sustainable Fisheries", path: "/sustainable-fisheries" },
            ]);
        }
    };

    const fetchPublications = async () => {
        try {
            const [articlesRes, materialsRes] = await Promise.all([
                invokeApi(config.getMyCollege + apiList.getArticles, {}, cookies),
                invokeApi(config.getMyCollege + apiList.getPdfPublications, {}, cookies),
            ]);

            const newItems = [{ label: "Articles", subitems: [] }];

            if (articlesRes?.status === 200 && articlesRes?.data?.articles) {
                const categories = [...new Set(articlesRes.data.articles.map(a => a.categoryName || a.category || a.category_name || a.title).filter(Boolean))];
                newItems[0].subitems = categories.map(c => ({
                    label: c,
                    path: `/publication/articles/${c.toLowerCase().replace(/\s+/g, '-')}`
                }));
            }

            if (materialsRes?.status === 200 && materialsRes?.data?.pdfs) {
                const pdfs = materialsRes.data.pdfs;
                const pdfsByCategory = {};
                pdfs.forEach((pdf) => {
                    const cat = pdf.categoryName || pdf.category || pdf.category_name;
                    if (cat) {
                        if (!pdfsByCategory[cat]) pdfsByCategory[cat] = {};
                        const sub = pdf.subcategory || pdf.subcategoryName || "Other";
                        if (!pdfsByCategory[cat][sub]) pdfsByCategory[cat][sub] = [];
                        pdfsByCategory[cat][sub].push(pdf);
                    }
                });

                Object.keys(pdfsByCategory).forEach((category) => {
                    const subcategoriesObj = pdfsByCategory[category];
                    const subcategoryNames = Object.keys(subcategoriesObj).sort((a, b) => {
                        if (a === "Other") return 1;
                        if (b === "Other") return -1;
                        return a.localeCompare(b);
                    });

                    newItems.push({
                        label: category,
                        subitems: subcategoryNames.map(sub => ({
                            label: sub,
                            path: `/publication/materials/${category.toLowerCase().replace(/ /g, "-")}/${sub.toLowerCase().replace(/ /g, "-")}`,
                        }))
                    });
                });
            }
            setPublicationItems(newItems);
        } catch (error) {
            console.error("Publications fetch error:", error);
            setPublicationItems([{ label: "Articles", subitems: [] }, { label: "Materials", subitems: [] }]);
        }
    };

    const fetchPeople = async () => {
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.getPeople,
                null,
                cookies
            );
            console.log("NavContext: People API Response:", response);
            if (response?.status === 200) {
                const resData = response.data.people;
                console.log("NavContext: People resData:", resData);
                let allPeople = [];

                if (Array.isArray(resData)) {
                    allPeople = resData;
                } else if (resData?.data && Array.isArray(resData.data)) {
                    allPeople = resData.data;
                } else if (resData?.people && Array.isArray(resData.people)) {
                    allPeople = resData.people;
                } else if (resData?.items && Array.isArray(resData.items)) {
                    allPeople = resData.items;
                }

                console.log("NavContext: Extracted people:", allPeople);

                const peopleByCategory = {};
                allPeople.forEach((person) => {
                    const category = person.category || "Other";
                    if (!peopleByCategory[category]) peopleByCategory[category] = [];
                    peopleByCategory[category].push({
                        label: person.name || "No Name",
                        path: `/people/${person.categoryUrl || person.ogUrl || ""}`,
                    });
                });

                const dynamicPeopleItems = Object.keys(peopleByCategory).map(category => ({
                    label: category,
                    path: `/people/category/${category.toLowerCase().replace(/\s+/g, "-")}`,
                }));

                const getRank = (label) => {
                    const l = label.toLowerCase();
                    if (l.includes("founder")) return 1;
                    if (l.includes("advisory")) return 2;
                    if (l.includes("administrative")) return 3;
                    if (l.includes("project")) return 4;
                    return 100;
                };

                dynamicPeopleItems.sort((a, b) => getRank(a.label) - getRank(b.label) || a.label.localeCompare(b.label));
                setPeopleItems(dynamicPeopleItems);
            }
        } catch (error) {
            console.error("People fetch error:", error);
            setPeopleItems([
                { label: "Founder Member", path: "/founder" },
                { label: "Advisory Board Members", path: "/advisory-board-members" },
                { label: "Administrative Member", path: "/administrative" },
                { label: "Project Member", path: "/project-members" },
            ]);
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchProgramme(), fetchPublications(), fetchPeople()]);
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    return (
        <NavContext.Provider value={{ programmeItems, publicationItems, peopleItems, loading }}>
            {children}
        </NavContext.Provider>
    );
};
